import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * P4: Nightcap lines (data-zv-cap, printed by snippets/quantity-selector.liquid) share ONE cap across lines: the offer splits the second set into its own
   * cart line, so the plus is disabled by the SUM of every Nightcap line, not each line alone. There is no render-time clamp (removed: a hand-made line
   * quantity above the cap is left as the cart has it; checkout is the authority).
   */
  /** @returns {CartQuantitySelectorComponent[]} every Nightcap cart-line selector in the document, in order */
  static capSelectors() {
    return /** @type {CartQuantitySelectorComponent[]} */ (
      Array.from(document.querySelectorAll('cart-quantity-selector-component[data-zv-cap]'))
    );
  }

  capValue() {
    return parseInt(this.refs.quantityInput.value, 10) || 0;
  }

  /**
   * Increase: a Nightcap line adds ONE unit of its variant through the add path that re-renders the drawer (assets/zv-nc-offer.js), because the offer
   * turns a quantity change into a second cart line; every other line keeps the theme's change.
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (Number(this.dataset.zvCap) && event.target instanceof HTMLElement) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('zv:nc-add', { detail: { variantId: this.dataset.variantId, source: this } }));
      return;
    }
    super.increaseQuantity(event);
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const cap = Number(this.dataset.zvCap);

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = cap
      ? CartQuantitySelectorComponent.capSelectors().reduce((n, el) => n + el.capValue(), 0) >= cap
      : effectiveMax !== null && value >= effectiveMax;
    // P4: the plus says so to assistive tech too (the stylesheet dims [aria-disabled="true"] as it dims :disabled)
    plusButton.setAttribute('aria-disabled', String(plusButton.disabled));
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
